import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const CHART_DASH_PATTERNS = ['0', '6 4', '3 3', '10 4 2 4', '12 6', '2 6'];

const buildLinePath = (values, minValue, maxValue, chartWidth, chartHeight, paddingX, paddingY) => {
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }

  const xStep = values.length > 1 ? chartWidth / (values.length - 1) : 0;
  const yRange = maxValue - minValue || 1;

  return values
    .map((value, index) => {
      if (value === null || value === undefined) {
        return null;
      }
      const x = paddingX + (index * xStep);
      const normalized = (Number(value) - minValue) / yRange;
      const y = paddingY + chartHeight - (normalized * chartHeight);
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(' ');
};

function LineTrendChart({ labels, series }) {
  const normalizedSeries = Array.isArray(series) ? series.filter((item) => Array.isArray(item.values)) : [];
  const valuePool = normalizedSeries.flatMap((item) => item.values).filter((value) => value !== null && value !== undefined);

  if (labels.length === 0 || normalizedSeries.length === 0 || valuePool.length === 0) {
    return <div className="empty-state">No balance trend data for this filter.</div>;
  }

  const minValue = Math.min(...valuePool);
  const maxValue = Math.max(...valuePool);
  const width = 880;
  const height = 280;
  const paddingX = 36;
  const paddingY = 18;
  const chartWidth = width - (paddingX * 2);
  const chartHeight = height - (paddingY * 2);
  const xTickStep = Math.max(1, Math.ceil(labels.length / 8));

  return (
    <>
      <div className="trend-chart-wrap">
        <svg viewBox={`0 0 ${width} ${height}`} className="trend-svg" role="img" aria-label="Account balance trend line chart">
          <line x1={paddingX} y1={paddingY} x2={paddingX} y2={paddingY + chartHeight} className="trend-axis" />
          <line x1={paddingX} y1={paddingY + chartHeight} x2={paddingX + chartWidth} y2={paddingY + chartHeight} className="trend-axis" />
          <text x={paddingX} y={paddingY + 2} className="trend-tick">{formatCurrency(maxValue)}</text>
          <text x={paddingX} y={paddingY + chartHeight - 4} className="trend-tick">{formatCurrency(minValue)}</text>

          {normalizedSeries.map((entry, index) => {
            const path = buildLinePath(entry.values, minValue, maxValue, chartWidth, chartHeight, paddingX, paddingY);
            if (!path) {
              return null;
            }
            return (
              <polyline
                key={entry.account_name}
                fill="none"
                points={path}
                className="trend-line"
                strokeDasharray={CHART_DASH_PATTERNS[index % CHART_DASH_PATTERNS.length]}
              />
            );
          })}

          {labels.map((label, index) => {
            if (index % xTickStep !== 0 && index !== labels.length - 1) {
              return null;
            }
            const x = paddingX + (labels.length > 1 ? (index * (chartWidth / (labels.length - 1))) : 0);
            return (
              <text key={`${label}-${index}`} x={x} y={height - 4} textAnchor="middle" className="trend-tick">{label}</text>
            );
          })}
        </svg>
      </div>

      <div className="trend-legend">
        {normalizedSeries.map((entry, index) => (
          <span key={entry.account_name} className="badge" style={{ borderStyle: 'solid', borderWidth: '1px', borderColor: 'rgba(188, 118, 197, 0.55)' }}>
            {entry.account_name} ({CHART_DASH_PATTERNS[index % CHART_DASH_PATTERNS.length] === '0' ? 'solid' : 'dashed'})
          </span>
        ))}
      </div>
    </>
  );
}

function SpendBarChart({ series, totalSpend }) {
  const rows = Array.isArray(series) ? series : [];
  const maxSpend = rows.reduce((max, row) => Math.max(max, Number(row.total_spend || 0)), 0);

  if (rows.length === 0) {
    return <div className="empty-state">No archived transaction spend for this filter.</div>;
  }

  return (
    <div className="spend-chart">
      <div className="summary-card" style={{ marginBottom: '1rem' }}>
        <p className="summary-label">Total Transaction Spend</p>
        <p className="summary-value">{formatCurrency(totalSpend)}</p>
      </div>
      <div className="spend-bars">
        {rows.map((row) => {
          const spend = Number(row.total_spend || 0);
          const widthPercent = maxSpend > 0 ? (spend / maxSpend) * 100 : 0;
          return (
            <div key={row.period} className="spend-row">
              <div className="spend-row-top">
                <span>{row.period}</span>
                <span>{formatCurrency(spend)}</span>
              </div>
              <div className="spend-track">
                <div className="spend-fill" style={{ width: `${widthPercent}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArchiveCoverageChart({ months }) {
  const yearMonthMap = useMemo(() => {
    const map = {};
    (Array.isArray(months) ? months : []).forEach((monthKey) => {
      if (typeof monthKey !== 'string') {
        return;
      }
      const [year, month] = monthKey.split('-');
      if (!year || !month) {
        return;
      }
      const monthInt = Number(month);
      if (!Number.isInteger(monthInt) || monthInt < 1 || monthInt > 12) {
        return;
      }
      if (!map[year]) {
        map[year] = new Set();
      }
      map[year].add(monthInt);
    });
    return map;
  }, [months]);

  const years = Object.keys(yearMonthMap).sort((a, b) => String(b).localeCompare(String(a)));
  const monthLabels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

  if (years.length === 0) {
    return <div className="empty-state">No archived months available yet.</div>;
  }

  return (
    <div className="archive-coverage">
      <div className="coverage-header-row">
        <span className="coverage-year-label">Year</span>
        <div className="coverage-month-header">
          {monthLabels.map((label, index) => (
            <span key={`${label}-${index}`} className="coverage-month-label">{label}</span>
          ))}
        </div>
      </div>

      {years.map((year) => {
        const monthSet = yearMonthMap[year] || new Set();
        const coveragePct = Math.round((monthSet.size / 12) * 100);
        return (
          <div key={year} className="coverage-year-row">
            <div className="coverage-year-meta">
              <span className="coverage-year">{year}</span>
              <span className="coverage-badge">{monthSet.size}/12 · {coveragePct}%</span>
            </div>
            <div className="coverage-month-grid">
              {Array.from({ length: 12 }, (_, idx) => idx + 1).map((monthNumber) => {
                const present = monthSet.has(monthNumber);
                const monthKey = `${year}-${String(monthNumber).padStart(2, '0')}`;
                return (
                  <span
                    key={monthKey}
                    className={`coverage-cell ${present ? 'is-present' : 'is-missing'}`}
                    title={`${monthKey} ${present ? 'archived' : 'missing'}`}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Analysis() {
  const user = localStorage.getItem('loggedInUsername');
  const [months, setMonths] = useState([]);
  const [trendView, setTrendView] = useState('month');
  const [spendMode, setSpendMode] = useState('all_transactions');
  const [trendYearFilter, setTrendYearFilter] = useState('');
  const [trendMonthFilter, setTrendMonthFilter] = useState('');
  const [trendData, setTrendData] = useState({ periods: [], account_series: [], spend_series: [], total_spend: 0 });
  const [isTrendBusy, setIsTrendBusy] = useState(false);
  const [trendStatusMessage, setTrendStatusMessage] = useState('');

  const getToken = () => localStorage.getItem('accessToken');

  const refreshArchiveMonths = async () => {
    const token = getToken();
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/archive/months', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user })
      });

      if (!response.ok) {
        throw new Error('Failed to load archive months');
      }

      const data = await response.json();
      const monthList = Array.isArray(data.months) ? data.months : [];
      setMonths(monthList);
    } catch (error) {
      setMonths([]);
    }
  };

  const refreshTrendView = async () => {
    const token = getToken();
    if (!token) {
      return;
    }

    try {
      setIsTrendBusy(true);
      setTrendStatusMessage('');

      const response = await fetch('/api/accounting/archive/trends', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          view: trendView,
          spend_mode: spendMode,
          year: trendYearFilter || null,
          month_key: trendMonthFilter || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load archive trends');
      }

      const data = await response.json();
      setTrendData({
        periods: Array.isArray(data.periods) ? data.periods : [],
        account_series: Array.isArray(data.account_series) ? data.account_series : [],
        spend_series: Array.isArray(data.spend_series) ? data.spend_series : [],
        total_spend: Number(data.total_spend || 0)
      });

      if (!Array.isArray(data.periods) || data.periods.length === 0) {
        setTrendStatusMessage('No trend data found for this filter.');
      }
    } catch (error) {
      setTrendData({ periods: [], account_series: [], spend_series: [], total_spend: 0 });
      setTrendStatusMessage('Unable to load archive trends.');
    } finally {
      setIsTrendBusy(false);
    }
  };

  useEffect(() => {
    refreshArchiveMonths();
  }, []);

  useEffect(() => {
    refreshTrendView();
  }, [trendView, spendMode, trendYearFilter, trendMonthFilter]);

  const sortedMonths = useMemo(() => {
    return [...new Set(Array.isArray(months) ? months : [])].sort((a, b) => String(b).localeCompare(String(a)));
  }, [months]);

  const availableYears = useMemo(() => {
    const years = new Set();
    sortedMonths.forEach((monthKey) => {
      if (typeof monthKey === 'string' && monthKey.length >= 4) {
        years.add(monthKey.slice(0, 4));
      }
    });
    return [...years].sort((a, b) => String(b).localeCompare(String(a)));
  }, [sortedMonths]);

  const filteredMonthOptions = useMemo(() => {
    if (!trendYearFilter) {
      return sortedMonths;
    }
    return sortedMonths.filter((monthKey) => String(monthKey).startsWith(`${trendYearFilter}-`));
  }, [sortedMonths, trendYearFilter]);

  useEffect(() => {
    if (trendMonthFilter && filteredMonthOptions.length > 0 && !filteredMonthOptions.includes(trendMonthFilter)) {
      setTrendMonthFilter('');
    }
  }, [trendMonthFilter, filteredMonthOptions]);

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Analysis</h2>
            <p className="section-subtitle">Visual analysis of archived balances, spend, and period coverage.</p>
          </div>
        </div>

        <section className="panel">
          <h3>Archive Visualizations</h3>
          <p className="panel-sub">Track account balance history and total archived transaction spend by month or year.</p>

          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: '1rem' }}>
            <label>
              View aggregation
              <select value={trendView} onChange={(event) => setTrendView(event.target.value)}>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </label>

            <label>
              Spend basis
              <select value={spendMode} onChange={(event) => setSpendMode(event.target.value)}>
                <option value="all_transactions">Total transactions</option>
                <option value="credit_only">Credit totals only</option>
                <option value="imported_only">Imported only (sheet match)</option>
              </select>
            </label>

            <label>
              Year filter
              <select value={trendYearFilter} onChange={(event) => setTrendYearFilter(event.target.value)}>
                <option value="">All years</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>

            {trendView === 'month' && (
              <label>
                Month filter
                <select value={trendMonthFilter} onChange={(event) => setTrendMonthFilter(event.target.value)}>
                  <option value="">All months</option>
                  {filteredMonthOptions.map((monthKey) => (
                    <option key={monthKey} value={monthKey}>{monthKey}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {isTrendBusy && <div className="loading-state">Loading trend data...</div>}
          {!isTrendBusy && (
            <>
              {trendStatusMessage && <div className="empty-state" style={{ marginBottom: '1rem' }}>{trendStatusMessage}</div>}

              <h4 style={{ margin: '0 0 0.75rem' }}>Balance Tracking by Account</h4>
              <LineTrendChart labels={trendData.periods || []} series={trendData.account_series || []} />

              <h4 style={{ margin: '1.2rem 0 0.75rem' }}>Total Transaction Spend</h4>
              <SpendBarChart series={trendData.spend_series || []} totalSpend={trendData.total_spend || 0} />

              <h4 style={{ margin: '1.2rem 0 0.75rem' }}>Archive Coverage Timeline</h4>
              <ArchiveCoverageChart months={sortedMonths} />
            </>
          )}
        </section>
      </section>
    </div>
  );
}

export default Analysis;
